import { HttpContextToken, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ProjectContextService } from '@core/services/project-context.service';

export const REQUEST_PROJECT_ID = new HttpContextToken<string | null>(() => null);
export const SKIP_PROJECT_SCOPE = new HttpContextToken<boolean>(() => false);

/**
 * Attaches scoping headers to API requests so the backend filters data to the user's context:
 *
 *  - **X-Project-Id**: a specific project is selected.
 *  - **X-Project-Tag**: a tag (e.g. "SIVAP") is selected → backend resolves to all projects
 *    that carry that tag and returns their combined data.
 *  - **X-Project-Location**: admin onshore/offshore leadership roll-up.
 *
 * These are mutually exclusive; priority: Project-Id > Tag > Location.
 * Project selection endpoints (api/projects*) are skipped.
 */
export const projectInterceptor: HttpInterceptorFn = (req, next) => {
  const ctx = inject(ProjectContextService);
  const path = new URL(req.url, 'http://localhost').pathname;

  if (path.includes('/projects')) {
    return next(req);
  }

  if (/^\/(?:api\/)?wells-npt\/supplier(?:\/|$)/.test(path)) {
    const projectId = ctx.currentProjectId();
    let headers = req.headers.delete('X-Project-Id').delete('X-Project-Tag').delete('X-Project-Location');
    if (projectId) headers = headers.set('X-Project-Id', projectId);
    return next(req.clone({ headers }));
  }

  if (req.context.get(SKIP_PROJECT_SCOPE) || /^\/(?:api\/)?wells-npt(?:\/|$)/.test(path)) {
    return next(req.clone({ headers: req.headers.delete('X-Project-Id').delete('X-Project-Tag').delete('X-Project-Location') }));
  }

  const requestedProjectId = req.context.get(REQUEST_PROJECT_ID);
  if (requestedProjectId) {
    return next(req.clone({ headers: req.headers.delete('X-Project-Tag').delete('X-Project-Location').set('X-Project-Id', requestedProjectId) }));
  }

  const projectId = ctx.currentProjectId();
  if (projectId) {
    return next(req.clone({ setHeaders: { 'X-Project-Id': projectId } }));
  }

  const tag = ctx.currentTagFilter();
  if (tag) {
    return next(req.clone({ setHeaders: { 'X-Project-Tag': tag } }));
  }

  const location = ctx.locationFilter();
  if (location) {
    return next(req.clone({ setHeaders: { 'X-Project-Location': location } }));
  }

  return next(req);
};
